import React from 'react';
import { useTranslation } from 'react-i18next';

import { asset } from '../../../../lib/asset';
import { Marks, Plate, PlateMark, StripeBadgeMark } from './BrandMarks.styles';

/**
 * The card option's third-party marks: the networks it accepts, and who processes it.
 *
 * All three are the OFFICIAL assets, bundled under `public/assets/config-v3/payment/` and served
 * through `asset()` like the rest of the checkout's payment art — not drawn here. That matters
 * for trademarks specifically: an approximation of a wordmark set in our own font is not the
 * logo, and a payment screen is the last place to ship one.
 *
 * `role="img"` + a label rather than `aria-hidden`: these identify the payment methods and who
 * handles the payment, so they carry meaning a screen reader needs. The Stripe badge is labelled
 * as a whole ("Powered by Stripe") because it is a lockup, not two words.
 */

/** The card networks the rail accepts.
 *
 *  Both marks are drawn by their owners for a LIGHT background — Visa's is `#1434cb` on nothing,
 *  which lands around 1.3:1 on the dark option card — so each sits on its own white plate. That
 *  is also how they appear on a physical card, and it makes one asset serve both studio themes. */
export const CardBrandMarks = (): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <Marks>
      <Plate>
        <PlateMark src={asset('payment/visa.svg')} alt={t('Visa')} $width={26} />
      </Plate>
      <Plate>
        <PlateMark src={asset('payment/mastercard.svg')} alt={t('Mastercard')} $width={22} />
      </Plate>
    </Marks>
  );
};

/**
 * Stripe's "Powered by Stripe" badge, in place of a sentence saying the same thing.
 *
 * The asset carries its own dark pill, so it needs no wrapper of ours — giving it one would put a
 * container inside a container for no gain.
 */
export const StripeBadge = (): React.ReactElement => {
  const { t } = useTranslation();
  return <StripeBadgeMark src={asset('payment/powered-by-stripe.svg')} alt={t('Powered by Stripe')} />;
};

export default CardBrandMarks;
